/**
 * Barra de filtros dos painéis.
 *
 * O período é sempre um intervalo (`de`/`ate`) — é o que cobre dia específico,
 * mês, vários meses e ano com um mecanismo só. A tela oferece atalhos porque no
 * celular ninguém quer preencher duas datas para ver "este mês"; o intervalo
 * livre fica a um toque, para quem precisa.
 *
 * Toda seleção abre em `Folha`, nunca no fluxo da página. Duas razões, ambas
 * aprendidas quebrando: um menu `absolute` era recortado pelo `overflow-x` que
 * a própria barra usa para rolar os chips, e um painel no fluxo empurrava os
 * gráficos para baixo, fazendo o usuário perder o lugar.
 */

import { useState } from 'react';
import { Building2, Calendar, Check, ChevronDown, User, X } from 'lucide-react';
import { Botao, Campo } from '../ui/index.tsx';
import Folha from '../ui/Folha.tsx';
import { cx } from '../../lib/cx.ts';

export interface ValorFiltro {
  de: string;
  ate: string;
  cooperativa?: string;
  consultor?: string;
  /** Só para exibição — qual atalho gerou o intervalo atual. */
  atalho?: string;
}

export interface OpcoesFiltro {
  cooperativas: { codigo: string; nome: string }[];
  consultores: { codigo: string; nome: string }[];
  travadoEmConsultor: string | null;
}

const iso = (d: Date): string => d.toLocaleDateString('en-CA', { timeZone: 'America/Cuiaba' });

function hojeLocal(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Cuiaba' }));
}

/**
 * Último dia do mês de uma data.
 *
 * Os atalhos de MÊS terminam no fim do mês, não em hoje — e isso importa muito.
 * Os vencimentos se concentram nos dias 5, 10, 15, 20, 25 e 30: no dia 11,
 * "até hoje" mostrava R$ 114 mil de um mês que emite R$ 352 mil, ou seja, um
 * terço. O painel parecia estar errando a conta quando na verdade estava
 * cortando o mês ao meio.
 *
 * Estender para o fim do mês não infla nada: adesão e pagamento não existem no
 * futuro, e a inadimplência tem sua própria trava de "já venceu".
 */
const fimDoMes = (d: Date): Date => new Date(d.getFullYear(), d.getMonth() + 1, 0);

/** Atalhos cobrindo os recortes pedidos: dia, mês, vários meses e ano. */
export function atalhos(): { id: string; rotulo: string; intervalo: () => [string, string] }[] {
  const h = () => hojeLocal();
  return [
    {
      id: 'hoje',
      rotulo: 'Hoje',
      intervalo: () => [iso(h()), iso(h())],
    },
    {
      id: '7d',
      rotulo: '7 dias',
      intervalo: () => {
        const i = h();
        i.setDate(i.getDate() - 6);
        return [iso(i), iso(h())];
      },
    },
    {
      id: 'mes',
      rotulo: 'Este mês',
      intervalo: () => [iso(new Date(h().getFullYear(), h().getMonth(), 1)), iso(fimDoMes(h()))],
    },
    {
      id: 'mes_ant',
      rotulo: 'Mês passado',
      intervalo: () => [
        iso(new Date(h().getFullYear(), h().getMonth() - 1, 1)),
        iso(new Date(h().getFullYear(), h().getMonth(), 0)),
      ],
    },
    {
      id: '3m',
      rotulo: '3 meses',
      intervalo: () => [iso(new Date(h().getFullYear(), h().getMonth() - 2, 1)), iso(fimDoMes(h()))],
    },
    {
      id: '12m',
      rotulo: '12 meses',
      intervalo: () => [
        iso(new Date(h().getFullYear(), h().getMonth() - 11, 1)),
        iso(fimDoMes(h())),
      ],
    },
    {
      id: 'ano',
      rotulo: 'Este ano',
      intervalo: () => [iso(new Date(h().getFullYear(), 0, 1)), iso(fimDoMes(h()))],
    },
  ];
}

export function filtroInicial(): ValorFiltro {
  const [de, ate] = atalhos().find((a) => a.id === 'mes')!.intervalo();
  return { de, ate, atalho: 'mes' };
}

type Aberta = 'periodo' | 'cooperativa' | 'consultor' | null;

export default function BarraFiltros({
  valor,
  onChange,
  opcoes,
  semPeriodo,
}: {
  valor: ValorFiltro;
  onChange: (v: ValorFiltro) => void;
  opcoes?: OpcoesFiltro;
  /** Para painéis que mostram o estado de agora, onde um período não significa nada. */
  semPeriodo?: boolean;
}) {
  const [aberta, setAberta] = useState<Aberta>(null);

  const rotuloPeriodo =
    atalhos().find((a) => a.id === valor.atalho)?.rotulo ??
    `${curto(valor.de)} – ${curto(valor.ate)}`;

  const coop = opcoes?.cooperativas.find((c) => c.codigo === valor.cooperativa);
  const cons = opcoes?.consultores.find((c) => c.codigo === valor.consultor);

  const mostraCooperativa = (opcoes?.cooperativas.length ?? 0) > 1;
  // Some para quem só enxerga as próprias vendas: o servidor já força o
  // recorte, e o seletor seria uma promessa falsa.
  const mostraConsultor = !opcoes?.travadoEmConsultor && (opcoes?.consultores.length ?? 0) > 1;

  return (
    <>
      <div className="scroll-x -mx-4 flex gap-2 px-4 md:mx-0 md:flex-wrap md:px-0">
        {!semPeriodo && (
          <Chip
            icone={<Calendar size={14} />}
            rotulo={rotuloPeriodo}
            ativo
            onAbrir={() => setAberta('periodo')}
          />
        )}

        {mostraCooperativa && (
          <Chip
            icone={<Building2 size={14} />}
            rotulo={coop?.nome ?? 'Cooperativa'}
            ativo={Boolean(coop)}
            onAbrir={() => setAberta('cooperativa')}
            onLimpar={coop ? () => onChange({ ...valor, cooperativa: undefined }) : undefined}
          />
        )}

        {mostraConsultor && (
          <Chip
            icone={<User size={14} />}
            rotulo={cons?.nome ?? 'Consultor'}
            ativo={Boolean(cons)}
            onAbrir={() => setAberta('consultor')}
            onLimpar={cons ? () => onChange({ ...valor, consultor: undefined }) : undefined}
          />
        )}
      </div>

      <Folha aberta={aberta === 'periodo'} titulo="Período" onFechar={() => setAberta(null)}>
        <ConteudoPeriodo
          valor={valor}
          onEscolher={(v) => {
            onChange({ ...valor, ...v });
            setAberta(null);
          }}
        />
      </Folha>

      <Folha
        aberta={aberta === 'cooperativa'}
        titulo="Cooperativa"
        onFechar={() => setAberta(null)}
      >
        <ListaOpcoes
          opcoes={(opcoes?.cooperativas ?? []).map((c) => ({ valor: c.codigo, rotulo: c.nome }))}
          selecionado={valor.cooperativa}
          rotuloTodos="Todas as cooperativas"
          onEscolher={(v) => {
            onChange({ ...valor, cooperativa: v });
            setAberta(null);
          }}
        />
      </Folha>

      <Folha aberta={aberta === 'consultor'} titulo="Consultor" onFechar={() => setAberta(null)}>
        <ListaOpcoes
          opcoes={(opcoes?.consultores ?? []).map((c) => ({ valor: c.codigo, rotulo: c.nome }))}
          selecionado={valor.consultor}
          rotuloTodos="Todos os consultores"
          busca
          onEscolher={(v) => {
            onChange({ ...valor, consultor: v });
            setAberta(null);
          }}
        />
      </Folha>
    </>
  );
}

// ─────────────────────────── Peças ───────────────────────────

function Chip({
  rotulo,
  icone,
  ativo,
  onAbrir,
  onLimpar,
}: {
  rotulo: string;
  icone?: React.ReactNode;
  ativo?: boolean;
  onAbrir: () => void;
  onLimpar?: () => void;
}) {
  return (
    <span
      className={cx(
        'inline-flex h-9 shrink-0 items-center rounded-[var(--r-md)] border text-[13px] font-medium transition-colors',
        ativo
          ? 'selecionado'
          : 'border-[var(--border-strong)] bg-[var(--surface)] text-[var(--muted)]',
      )}
    >
      <button
        onClick={onAbrir}
        className="anel-foco inline-flex h-full items-center gap-1.5 rounded-[var(--r-md)] pl-3 pr-2"
      >
        {icone}
        <span className="max-w-[44vw] truncate md:max-w-[220px]">{rotulo}</span>
        {!onLimpar && <ChevronDown size={13} className="opacity-70" />}
      </button>
      {onLimpar && (
        <button
          onClick={onLimpar}
          aria-label={`Remover filtro ${rotulo}`}
          className="anel-foco grid h-full w-7 place-items-center rounded-r-[var(--r-md)]"
        >
          <X size={13} />
        </button>
      )}
    </span>
  );
}

function ListaOpcoes({
  opcoes,
  selecionado,
  rotuloTodos,
  busca,
  onEscolher,
}: {
  opcoes: { valor: string; rotulo: string }[];
  selecionado?: string;
  rotuloTodos: string;
  busca?: boolean;
  onEscolher: (v: string | undefined) => void;
}) {
  const [termo, setTermo] = useState('');
  const filtradas = termo
    ? opcoes.filter((o) => o.rotulo.toLowerCase().includes(termo.toLowerCase()))
    : opcoes;

  return (
    <div className="flex flex-col gap-2">
      {/* Busca só onde a lista é longa — 33 consultores não se acham rolando.
          Fica grudada no topo: com o teclado aberto a área útil é pequena, e o
          campo rolar para fora enquanto se digita é desorientador. */}
      {busca && (
        <div className="sticky top-0 z-10 -mx-3 -mt-3 bg-[var(--surface)] px-3 pt-3 pb-2">
          <input
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            placeholder="Buscar…"
            aria-label="Buscar"
            autoComplete="off"
            className="anel-foco h-10 w-full rounded-[var(--r-md)] border border-[var(--border-strong)] bg-[var(--surface)] px-3 text-base text-[var(--fg)] placeholder:text-[var(--faint)] sm:text-sm"
          />
        </div>
      )}

      <ul className="m-0 flex list-none flex-col p-0">
        <li>
          <Opcao rotulo={rotuloTodos} marcado={!selecionado} onClick={() => onEscolher(undefined)} />
        </li>
        {filtradas.map((o) => (
          <li key={o.valor}>
            <Opcao
              rotulo={o.rotulo}
              marcado={o.valor === selecionado}
              onClick={() => onEscolher(o.valor === selecionado ? undefined : o.valor)}
            />
          </li>
        ))}
        {filtradas.length === 0 && (
          <li className="px-3 py-6 text-center text-[13px] text-[var(--muted)]">
            Nada encontrado para “{termo}”.
          </li>
        )}
      </ul>
    </div>
  );
}

function Opcao({
  rotulo,
  marcado,
  onClick,
}: {
  rotulo: string;
  marcado: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      // 44px: alvo de toque confortável para o polegar.
      aria-pressed={marcado}
      className={cx(
        'anel-foco flex min-h-[44px] w-full items-center gap-2.5 rounded-[var(--r-sm)] border border-transparent px-2.5 text-left text-sm text-[var(--fg)]',
        marcado ? 'selecionado font-medium' : 'hover:bg-[var(--surface-hover)]',
      )}
    >
      <Check size={15} className={marcado ? 'text-[var(--brand)]' : 'opacity-0'} />
      <span className="min-w-0 flex-1 truncate">{rotulo}</span>
    </button>
  );
}

function ConteudoPeriodo({
  valor,
  onEscolher,
}: {
  valor: ValorFiltro;
  onEscolher: (v: { de: string; ate: string; atalho?: string }) => void;
}) {
  const [de, setDe] = useState(valor.de);
  const [ate, setAte] = useState(valor.ate);

  return (
    <div className="flex flex-col gap-4">
      <div className="sem-orfao grid grid-cols-2 gap-1.5">
        {atalhos().map((a) => (
          <button
            key={a.id}
            onClick={() => {
              const [d, t] = a.intervalo();
              onEscolher({ de: d, ate: t, atalho: a.id });
            }}
            className={cx(
              'anel-foco h-10 rounded-[var(--r-md)] border px-2 text-[13px] font-medium transition-colors',
              valor.atalho === a.id
                ? 'selecionado'
                : 'border-[var(--border)] text-[var(--muted)] hover:text-[var(--fg)]',
            )}
          >
            {a.rotulo}
          </button>
        ))}
      </div>

      <div className="border-t border-[var(--border)] pt-4">
        <p className="mb-3 text-[13px] text-[var(--muted)]">Ou escolha um intervalo:</p>
        <div className="flex gap-2">
          <Campo
            rotulo="De"
            type="date"
            value={de}
            max={ate}
            onChange={(e) => setDe(e.target.value)}
          />
          <Campo
            rotulo="Até"
            type="date"
            value={ate}
            min={de}
            onChange={(e) => setAte(e.target.value)}
          />
        </div>
        <Botao
          variante="primario"
          larguraTotal
          className="mt-3"
          disabled={!de || !ate || de > ate}
          onClick={() => onEscolher({ de, ate, atalho: undefined })}
        >
          Aplicar intervalo
        </Botao>
      </div>
    </div>
  );
}

function curto(iso: string): string {
  const [a, m, d] = iso.split('-');
  return `${d}/${m}/${a?.slice(2)}`;
}
